// db/templates.ts
import { DesignSystem } from "@adornis/chemistry/elements/theming/design.js";
import { html, nothing } from "lit";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
var buildEmail = (options) => base({
  title: options.title,
  style: emailStyles(options.colorConfig, options.customStyle),
  content: mainSection(
    (Array.isArray(options.contents) ? options.contents : [options.contents]).map(
      (cont, index, array) => index === array.length - 1 ? contentNoBlock(cont) : content(cont)
    ),
    options.logoConfig
  ),
  footer: options.footer
});
var base = ({
  title,
  style,
  content: content2,
  footer: footer2
}) => html`
  <!DOCTYPE html>
  <html lang="en" dir="ltr" xmlns:v="urn:schemas-microsoft-com:vml" xmlns:o="urn:schemas-microsoft-com:office:office">
    <head>
      <meta charset="utf-8" />
      <meta http-equiv="X-UA-Compatible" content="IE=edge" />
      <meta name="viewport" content="width=device-width,initial-scale=1 user-scalable=yes" />
      <meta name="format-detection" content="telephone=no, date=no, address=no, email=no, url=no" />
      <meta name="x-apple-disable-message-reformatting" />
      ${unsafeHTML(`<title>${title}</title>`)}
      <!--[if mso]>
        <noscript>
          <xml>
            <o:OfficeDocumentSettings><o:PixelsPerInch>96</o:PixelsPerInch></o:OfficeDocumentSettings>
          </xml>
        </noscript>
      <![endif]-->
      <!--[if mso]>
        <style>
          table,
          tr,
          td,
          p,
          span,
          a {
            mso-line-height-rule: exactly !important;
            line-height: 120% !important;
            mso-table-lspace: 0 !important;
            mso-table-rspace: 0 !important;
          }
        </style>
      <![endif]-->
      ${unsafeHTML(style?.toString() || "")}
    </head>

    <body itemscope itemtype="http://schema.org/EmailMessage">
      <table class="body-wrap">
        <tr>
          <td></td>
          <td class="container">
            <div class="content">
              ${content2}
              <div class="footer">${footer2}</div>
            </div>
          </td>
          <td></td>
        </tr>
      </table>
    </body>
  </html>
`;
var mainSection = (content2, logoConfig) => html`
  <table class="main" width="100%" cellpadding="0" cellspacing="0">
    <tr class="topLine">
      <td></td>
    </tr>
    ${logoConfig ? html`
          <tr class="logo">
            <td align="center">
              <img
                src="${logoConfig.src}"
                alt="${logoConfig.alt}"
                style="height: auto; padding: ${logoConfig.padding ?? 0}px;"
                width="${logoConfig.width ?? 600}"
              />
            </td>
          </tr>
        ` : nothing}
    <tr>
      <td class="content-wrap">
        <table width="100%" cellpadding="0" cellspacing="0">
          ${content2}
        </table>
      </td>
    </tr>
  </table>
`;
var content = (content2) => html`
  <tr>
    <td class="content-block">${content2}</td>
  </tr>
`;
var contentNoBlock = (content2) => html`
  <tr>
    <td>${content2}</td>
  </tr>
`;
var footer = (content2) => html`
  <div class="footer">
    <table width="100%">
      ${content2}
    </table>
  </div>
`;
var footerContent = (content2) => html`
  <tr>
    <td class="aligncenter content-block">${content2}</td>
  </tr>
`;
var emailStyles = ({ wrapperBack, mainBack, mainBorder, highlight } = {}, customStyles) => {
  const designSystem = DesignSystem.currentTheme.getValue();
  wrapperBack ??= designSystem.colors.neutral.shade(100);
  mainBack ??= designSystem.colors.neutral.shade(50);
  mainBorder ??= designSystem.colors.neutral.shade(300);
  highlight ??= designSystem.colors.accent.string;
  const baseStyles = `
/* -------------------------------------
    GLOBAL
    A very basic CSS reset
------------------------------------- */
* {
  margin: 0;
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  box-sizing: border-box;
  font-size: 14px;
}

@media print {
  .page-break {page-break-after: always;}
}

img {
  max-width: 100%;
}

html, html * {
  -webkit-print-color-adjust: exact;
}

body {
  -webkit-font-smoothing: antialiased;
  -webkit-text-size-adjust: none;
  width: 100% !important;
  height: 100%;
  line-height: 1.6em;
  /* 1.6em * 14px = 22.4px, use px to get airier line-height also in Thunderbird, and Yahoo!, Outlook.com, AOL webmail clients */
  /*line-height: 22px;*/
}

/* Let's make sure all tables have defaults */
table td {
  vertical-align: top;
}

/* avoid bleeding tables across page breaks: https://stackoverflow.com/questions/1542320/margin-while-printing-html-page */
table { page-break-inside:auto }
tr    { page-break-inside:avoid; page-break-after:auto }
thead { display:table-header-group }
tfoot { display:table-footer-group }

/* -------------------------------------
    BODY & CONTAINER
------------------------------------- */
body {
  background-color: ${wrapperBack};
}

.body-wrap {
  background-color: ${wrapperBack};
  width: 100%;
}

.container {
  display: block !important;
  max-width: 600px !important;
  margin: 0 auto !important;
  /* makes it centered */
  clear: both !important;
}

.content {
  max-width: 600px;
  margin: 0 auto;
  display: block;
  padding: 20px;
}

/* -------------------------------------
    HEADER, FOOTER, MAIN
------------------------------------- */
.main {
  background-color: ${mainBack};
  border: 1px solid ${mainBorder};
  border-radius: 3px;
}

.content-wrap {
  padding: 20px;
}

.content-block {
  padding: 0 0 20px;
}

.header {
  width: 100%;
  margin-bottom: 20px;
}

.logo {
  padding-top: 40px;
  height: 120px;
}

.logo img {
  display: block;
  margin: 0 auto 0 auto;
}

.topLine {
  background-color: ${highlight};
  height: 3px;
}

.highlighted {
  background: #ff5100;
  border-radius: 10px;
  padding: 10px;
}

.highlighted.red { background: #ef6f6c; }

.highlighted.green { background: #56e39f; }

.green {
  background: #ef6f6c;
}

.red {
  background: #56e39f;
}

.grey {
  color: #999;
}

.tableSpace {
  height: 6px;
}

.productTable {
  border: solid 1px #999;
  border-radius: 10px;
  padding: 10px;
}

.footer {
  width: 100%;
  clear: both;
  color: #999;
  padding: 20px;
}
.footer p,
.footer a,
.footer td {
  color: #999;
  font-size: 12px;
  padding: 0px;
}

/* -------------------------------------
    TYPOGRAPHY
------------------------------------- */
h1,
h2,
h3 {
  font-family: 'Helvetica Neue', Helvetica, Arial, 'Lucida Grande', sans-serif;
  color: #000;
  margin: 40px 0 0;
  line-height: 1.2em;
  font-weight: 400;
}

h1 {
  font-size: 32px;
  font-weight: 500;
  /* 1.2em * 32px = 38.4px, use px to get airier line-height also in Thunderbird, and Yahoo!, Outlook.com, AOL webmail clients */
  /*line-height: 38px;*/
}

h2 {
  font-size: 24px;
  /* 1.2em * 24px = 28.8px, use px to get airier line-height also in Thunderbird, and Yahoo!, Outlook.com, AOL webmail clients */
  /*line-height: 29px;*/
}

h3 {
  font-size: 18px;
  /* 1.2em * 18px = 21.6px, use px to get airier line-height also in Thunderbird, and Yahoo!, Outlook.com, AOL webmail clients */
  /*line-height: 22px;*/
}

h4 {
  font-size: 14px;
  font-weight: 600;
}

p,
ul,
ol {
  margin-bottom: 10px;
  font-weight: normal;
}
p li,
ul li,
ol li {
  margin-left: 5px;
  list-style-position: outside;
}

.small {
  font-size: 11px;
}

.smaller {
  font-size: 9px;
}

/* -------------------------------------
    LINKS & BUTTONS
------------------------------------- */
a {
  color: ${highlight};
  text-decoration: underline;
}

.btn-primary {
  width: 100%;
  text-decoration: none;
  color: #fff;
  background-color: ${highlight};
  border: solid ${highlight};
  border-width: 10px 20px;
  line-height: 2em;
  /* 2em * 14px = 28px, use px to get airier line-height also in Thunderbird, and Yahoo!, Outlook.com, AOL webmail clients */
  /*line-height: 28px;*/
  font-weight: bold;
  text-align: center;
  cursor: pointer;
  display: inline-block;
  border-radius: 5px;
}

.btn-secondary {
  padding: 0px 20px;
  text-decoration: none;
  color: ${highlight};
  border: solid ${highlight};
  border-width: 1px;
  line-height: 2em;
  /* 2em * 14px = 28px, use px to get airier line-height also in Thunderbird, and Yahoo!, Outlook.com, AOL webmail clients */
  /*line-height: 28px;*/
  font-weight: bold;
  text-align: center;
  cursor: pointer;
  display: inline-block;
  border-radius: 5px;
}

/* -------------------------------------
    OTHER STYLES THAT MIGHT BE USEFUL
------------------------------------- */
.last {
  margin-bottom: 0;
}

.first {
  margin-top: 0;
}

.aligncenter {
  text-align: center;
}

.alignright {
  text-align: right;
}

.alignleft {
  text-align: left;
}

.clear {
  clear: both;
}

/* -------------------------------------
    ALERTS
    Change the class depending on warning email, good email or bad email
------------------------------------- */
.alert {
  font-size: 16px;
  color: #fff;
  font-weight: 500;
  padding: 20px;
  text-align: center;
  border-radius: 3px 3px 0 0;
}
.alert a {
  color: #fff;
  text-decoration: none;
  font-weight: 500;
  font-size: 16px;
}
.alert.alert-warning {
  background-color: #ff9f00;
}
.alert.alert-bad {
  background-color: #d0021b;
}
.alert.alert-good {
  background-color: #68b90f;
}

/* -------------------------------------
    INVOICE
    Styles for the billing table
------------------------------------- */
.invoice {
  margin: 40px auto;
  text-align: left;
  width: 80%;
}
.invoice td {
  padding: 5px 0;
}
.invoice .invoice-items {
  width: 100%;
}
.invoice .invoice-items td {
  border-top: #eee 1px solid;
}
.invoice .invoice-items .total td {
  border-top: 2px solid #333;
  border-bottom: 2px solid #333;
  font-weight: 700;
}

/* -------------------------------------
    RESPONSIVE AND MOBILE FRIENDLY STYLES
------------------------------------- */
@media only screen and (max-width: 640px) {
  body {
    padding: 0 !important;
  }

  h1,
  h2,
  h3,
  h4 {
    font-weight: 800 !important;
    margin: 20px 0 5px !important;
  }

  h1 {
    font-size: 22px !important;
  }

  h2 {
    font-size: 18px !important;
  }

  h3 {
    font-size: 16px !important;
  }

  .container {
    padding: 0 !important;
    width: 100% !important;
  }

  .content {
    padding: 0 !important;
  }

  .content-wrap {
    padding: 10px !important;
  }

  .invoice {
    width: 100% !important;
  }
}
`;
  return `
    <style>
      ${baseStyles}
      ${customStyles}
    </style>
  `;
};
export {
  base,
  buildEmail,
  content,
  contentNoBlock,
  emailStyles,
  footer,
  footerContent,
  mainSection
};
